import React, { useState, useMemo } from "react";
import Page from "../components/Page";
import {
  IconFileSpreadsheet,
  IconPrinter,
  IconLock,
  IconLockOpen,
  IconReceiptTax,
  IconScale,
  IconTrendingUp,
  IconTrendingDown,
  IconArrowRight,
  IconCheck,
  IconAlertTriangle,
  IconCalendar,
  IconShieldCheck
} from "@tabler/icons-react";
import {
  useMasikabari,
  closeAccountingPeriod,
  reopenAccountingPeriod
} from "../controllers/accounting.controller";
import {
  NEPALI_MONTHS,
  getCurrentBsDate,
  getAvailableFiscalYears
} from "../helpers/NepaliDateHelper";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";

export default function MasikabariPage() {
  const currentBs = useMemo(() => getCurrentBsDate(), []);
  const fiscalYears = useMemo(() => getAvailableFiscalYears(), []);

  const [selectedFy, setSelectedFy] = useState(currentBs.fiscalYear || "2083/84");
  const [selectedMonth, setSelectedMonth] = useState(currentBs.bsMonth || 1);

  const [closeNotes, setCloseNotes] = useState("");
  const [reopenReason, setReopenReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, period, salesSummary, purchaseSummary, vatSummary, financialSummary, isLoading, mutate } =
    useMasikabari({
      fiscalYear: selectedFy,
      nepaliMonth: selectedMonth
    });

  const monthObj = useMemo(
    () => NEPALI_MONTHS.find((m) => m.id === Number(selectedMonth)) || NEPALI_MONTHS[0],
    [selectedMonth]
  );

  const isPeriodClosed = period?.status === "CLOSED";

  // Handle Close Period
  const handleClosePeriod = async () => {
    try {
      setIsSubmitting(true);
      toast.loading(`Closing and locking ${monthObj.name} ${selectedFy}...`);
      await closeAccountingPeriod({
        fiscal_year: selectedFy,
        nepali_month: selectedMonth,
        notes: closeNotes || `Monthly closure for ${monthObj.name} ${selectedFy}`
      });
      toast.dismiss();
      toast.success(`${monthObj.name} ${selectedFy} closed and locked successfully!`);
      mutate();
      document.getElementById("modal-close-period").close();
      setCloseNotes("");
    } catch (err) {
      toast.dismiss();
      toast.error(err?.response?.data?.message || "Failed to close accounting period");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Reopen Period
  const handleReopenPeriod = async () => {
    try {
      setIsSubmitting(true);
      toast.loading(`Reopening ${monthObj.name} ${selectedFy}...`);
      await reopenAccountingPeriod({
        fiscal_year: selectedFy,
        nepali_month: selectedMonth,
        reason: reopenReason || `Administrative audit adjustment`
      });
      toast.dismiss();
      toast.success(`${monthObj.name} ${selectedFy} reopened for modifications!`);
      mutate();
      document.getElementById("modal-reopen-period").close();
      setReopenReason("");
    } catch (err) {
      toast.dismiss();
      toast.error(err?.response?.data?.message || "Failed to reopen accounting period");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Export Masikabari to Excel
  const handleExportExcel = () => {
    try {
      const rows = [
        ["MASIKABARI (MONTHLY VAT RETURN & CLOSING SUMMARY)"],
        [`Fiscal Year: ${selectedFy} | Month: ${monthObj.name} (${monthObj.nepaliName})`],
        [`Period Status: ${isPeriodClosed ? "CLOSED & LOCKED" : "OPEN"}`],
        [],
        ["1. SALES BREAKDOWN (बिक्री विवरण)"],
        ["Taxable Sales (13% VAT Base)", Number(salesSummary.taxable_sales || 0).toFixed(2)],
        ["Output VAT Collected (१३% बिक्री कर)", Number(salesSummary.output_vat || 0).toFixed(2)],
        ["Service Charge Collected (सेवा शुल्क)", Number(salesSummary.service_charge || 0).toFixed(2)],
        ["Exempt Sales (कर छुट बिक्री)", Number(salesSummary.exempt_sales || 0).toFixed(2)],
        ["Total Net Sales", Number(salesSummary.total_sales || 0).toFixed(2)],
        [],
        ["2. PURCHASE BREAKDOWN (खरिद विवरण)"],
        ["Taxable Purchases", Number(purchaseSummary.taxable_purchases || 0).toFixed(2)],
        ["Input VAT Paid (१३% खरिद कर)", Number(purchaseSummary.input_vat || 0).toFixed(2)],
        ["Exempt Purchases (कर छुट खरिद)", Number(purchaseSummary.exempt_purchases || 0).toFixed(2)],
        ["Total Purchases", Number(purchaseSummary.total_purchases || 0).toFixed(2)],
        [],
        ["3. VAT POSITION RECONCILIATION (कर मिलान)"],
        ["Total Output VAT Collected", Number(vatSummary.output_vat || 0).toFixed(2)],
        ["Less: Input VAT Credit Claimed", Number(vatSummary.input_vat || 0).toFixed(2)],
        [
          vatSummary.is_payable ? "Net VAT Payable to IRD (बुझाउनु पर्ने भ्याट)" : "Net VAT Credit Carryforward (अर्को महिनामा मिलान हुने)",
          Number(vatSummary.net_vat || 0).toFixed(2)
        ],
        [],
        ["4. FINANCIAL PERFORMANCE OVERVIEW"],
        ["Total Sales Revenue", Number(financialSummary.gross_sales || 0).toFixed(2)],
        ["Less: Purchases (COGS)", Number(financialSummary.cogs_purchases || 0).toFixed(2)],
        ["Gross Operating Margin", Number(financialSummary.gross_profit || 0).toFixed(2)],
        ["Operating Expenses & Overhead", Number(financialSummary.operating_expenses || 0).toFixed(2)],
        ["Net Operating Income", Number(financialSummary.net_profit || 0).toFixed(2)]
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Masikabari");
      XLSX.writeFile(workbook, `Masikabari_${selectedFy.replace("/", "-")}_${monthObj.name}.xlsx`);
      toast.success("Masikabari exported to Excel successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to export Excel");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Page className="print:p-0 print:bg-white">
      {/* ─── SCREEN-ONLY HEADER ─── */}
      <div className="space-y-6 print:hidden">
        {/* Banner Card */}
        <div className="bg-gradient-to-r from-purple-800 via-indigo-800 to-slate-900 rounded-3xl p-6 md:p-8 text-white shadow-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-md text-xs font-black uppercase tracking-wider mb-2">
              <IconScale size={14} /> मासिक विवरण (Masikabari)
            </div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight">
              Masikabari (Monthly VAT & Closing)
            </h1>
            <p className="text-purple-100 text-sm mt-1 max-w-xl">
              Consolidated monthly tax reconciliation for Nepal Inland Revenue Department (IRD). Reconciles Bikri Khata vs Kharid Khata, computes net VAT liability or credit carryforward, and provides period-lock governance.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            {isPeriodClosed ? (
              <button
                onClick={() => document.getElementById("modal-reopen-period").showModal()}
                className="py-3 px-5 bg-amber-500 hover:bg-amber-600 text-gray-950 rounded-2xl font-black text-xs flex items-center gap-2 transition shadow-lg shadow-black/10 active:scale-95"
              >
                <IconLockOpen size={18} /> Reopen Month
              </button>
            ) : (
              <button
                onClick={() => document.getElementById("modal-close-period").showModal()}
                className="py-3 px-5 bg-rose-600 hover:bg-rose-700 text-white rounded-2xl font-black text-xs flex items-center gap-2 transition shadow-lg shadow-rose-900/30 active:scale-95"
              >
                <IconLock size={18} /> Close & Lock Month
              </button>
            )}

            <button
              onClick={handleExportExcel}
              className="py-3 px-5 bg-white text-gray-900 hover:bg-gray-100 rounded-2xl font-black text-xs flex items-center gap-2 transition shadow-lg shadow-black/10 active:scale-95"
            >
              <IconFileSpreadsheet size={18} className="text-emerald-600" /> Export Excel
            </button>

            <button
              onClick={handlePrint}
              className="py-3 px-5 bg-white/10 hover:bg-white/20 text-white rounded-2xl font-black text-xs flex items-center gap-2 transition border border-white/20 active:scale-95"
            >
              <IconPrinter size={18} /> Print
            </button>
          </div>
        </div>

        {/* Fiscal Year & Month Selector */}
        <div className="bg-white dark:bg-restro-gray border border-gray-100 dark:border-gray-800 rounded-3xl p-5 shadow-sm space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="text-xs font-black uppercase tracking-wider text-gray-400 flex items-center gap-1.5 shrink-0">
                <IconCalendar size={16} className="text-purple-500" /> Fiscal Year:
              </span>
              <select
                value={selectedFy}
                onChange={(e) => setSelectedFy(e.target.value)}
                className="select select-sm select-bordered rounded-xl bg-gray-50 dark:bg-gray-800 font-black text-xs outline-none"
              >
                {fiscalYears.map((fy) => (
                  <option key={fy} value={fy}>
                    FY {fy} (B.S.)
                  </option>
                ))}
              </select>
            </div>

            {/* Status Indicator Badge */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 font-bold">Month Status:</span>
              {isPeriodClosed ? (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-black bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 border border-rose-300 dark:border-rose-900">
                  <IconLock size={14} /> LOCKED ({period?.closed_at ? new Date(period.closed_at).toLocaleDateString() : "Closed"})
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-black bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-900">
                  <IconLockOpen size={14} /> OPEN FOR POSTING
                </span>
              )}
            </div>
          </div>

          {/* 12 Nepali Month Pills */}
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-1.5">
            {NEPALI_MONTHS.map((m) => (
              <button
                key={m.id}
                onClick={() => setSelectedMonth(m.id)}
                className={`py-2 px-1 text-center rounded-xl text-xs font-bold transition ${
                  selectedMonth === m.id
                    ? "bg-purple-600 text-white shadow-md shadow-purple-500/20 font-black"
                    : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                }`}
              >
                <div>{m.name}</div>
                <div className="text-[10px] opacity-70">{m.nepaliName}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ─── PRINT-ONLY STATUTORY HEADER ─── */}
      <div className="hidden print:block mb-6 text-center space-y-1">
        <h2 className="text-xl font-bold uppercase">Masikabari (मासिक कर तथा आर्थिक विवरण)</h2>
        <h3 className="text-sm font-semibold">नेपाल सरकार आन्तरिक राजस्व विभाग (IRD)</h3>
        <p className="text-xs">
          आ.व.: {selectedFy} | महिना: {monthObj.name} ({monthObj.nepaliName}) | स्थिति:{" "}
          {isPeriodClosed ? "बन्द तथा प्रमाणीकृत (CLOSED)" : "खुला (OPEN)"}
        </p>
      </div>

      {/* ─── MAIN DASHBOARD CONTENT ─── */}
      <div className="space-y-6 mt-6">
        {/* Net VAT Position Hero Card */}
        <div
          className={`rounded-3xl p-6 md:p-8 border shadow-lg ${
            vatSummary.is_payable
              ? "bg-gradient-to-br from-amber-500/10 via-rose-500/10 to-transparent border-rose-200 dark:border-rose-900/50"
              : "bg-gradient-to-br from-emerald-500/10 via-teal-500/10 to-transparent border-emerald-200 dark:border-emerald-900/50"
          }`}
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <span className="text-xs font-black uppercase tracking-wider text-gray-400 block mb-1">
                Net VAT Position for {monthObj.name} {selectedFy}
              </span>
              <h2 className="text-3xl md:text-4xl font-black text-gray-900 dark:text-white">
                Rs. {Number(vatSummary.net_vat || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </h2>
              <p
                className={`text-sm font-bold mt-1 ${
                  vatSummary.is_payable ? "text-rose-600 dark:text-rose-400" : "text-emerald-600 dark:text-emerald-400"
                }`}
              >
                {vatSummary.is_payable
                  ? "Net VAT Payable to Government of Nepal (IRD)"
                  : "Net VAT Credit Available for Carryforward to Next Month"}
              </p>
            </div>

            <div className="flex items-center gap-6 divide-x divide-gray-200 dark:divide-gray-800">
              <div className="pr-2">
                <span className="text-[10px] font-black uppercase text-indigo-500 block">Output VAT Collected</span>
                <span className="text-xl font-black text-gray-900 dark:text-white">
                  Rs. {Number(vatSummary.output_vat || 0).toFixed(2)}
                </span>
                <span className="text-[10px] text-gray-400 block">From Bikri Khata</span>
              </div>
              <div className="pl-6">
                <span className="text-[10px] font-black uppercase text-teal-500 block">Input VAT Claimed</span>
                <span className="text-xl font-black text-gray-900 dark:text-white">
                  Rs. {Number(vatSummary.input_vat || 0).toFixed(2)}
                </span>
                <span className="text-[10px] text-gray-400 block">From Kharid Khata</span>
              </div>
            </div>
          </div>
        </div>

        {/* Two-Column Reconciliation Section: Sales vs Purchases */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Bikri Bibaran (Sales Book Summary) */}
          <div className="bg-white dark:bg-restro-gray rounded-3xl border border-gray-100 dark:border-gray-800 p-6 shadow-sm space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-blue-100 text-blue-600 dark:bg-blue-950/60 dark:text-blue-300 flex items-center justify-center font-black">
                  १
                </div>
                <div>
                  <h3 className="font-black text-base text-gray-900 dark:text-white">
                    Bikri Bibaran (बिक्री विवरण)
                  </h3>
                  <p className="text-[11px] text-gray-400">Schedule 8 Sales Reconciliation</p>
                </div>
              </div>
              <span className="text-xs font-mono font-bold text-gray-500">
                {salesSummary.invoice_count || 0} Invoices
              </span>
            </div>

            <div className="space-y-3 text-xs">
              <div className="flex justify-between py-2 border-b border-gray-50 dark:border-gray-800">
                <span className="text-gray-500 font-bold">Taxable Sales (करयोग्य बिक्री):</span>
                <span className="font-bold text-gray-900 dark:text-white">
                  Rs. {Number(salesSummary.taxable_sales || 0).toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between py-2 border-b border-gray-50 dark:border-gray-800">
                <span className="text-indigo-600 font-bold">13% Output VAT (बिक्री कर):</span>
                <span className="font-bold text-indigo-600">
                  Rs. {Number(salesSummary.output_vat || 0).toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between py-2 border-b border-gray-50 dark:border-gray-800">
                <span className="text-amber-600 font-bold">Service Charge (१०% सेवा शुल्क):</span>
                <span className="font-bold text-amber-600">
                  Rs. {Number(salesSummary.service_charge || 0).toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between py-2 border-b border-gray-50 dark:border-gray-800">
                <span className="text-gray-400 font-bold">Exempt Sales (कर छुट बिक्री):</span>
                <span className="font-bold text-gray-500">
                  Rs. {Number(salesSummary.exempt_sales || 0).toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between py-3 border-t-2 border-gray-200 dark:border-gray-700 text-sm font-black">
                <span className="text-gray-900 dark:text-white">Total Sales (जम्मा बिक्री):</span>
                <span className="text-blue-600">
                  Rs. {Number(salesSummary.total_sales || 0).toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Kharid Bibaran (Purchase Book Summary) */}
          <div className="bg-white dark:bg-restro-gray rounded-3xl border border-gray-100 dark:border-gray-800 p-6 shadow-sm space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-300 flex items-center justify-center font-black">
                  २
                </div>
                <div>
                  <h3 className="font-black text-base text-gray-900 dark:text-white">
                    Kharid Bibaran (खरिद विवरण)
                  </h3>
                  <p className="text-[11px] text-gray-400">Schedule 9 Purchase Reconciliation</p>
                </div>
              </div>
              <span className="text-xs font-mono font-bold text-gray-500">
                {purchaseSummary.bill_count || 0} Bills
              </span>
            </div>

            <div className="space-y-3 text-xs">
              <div className="flex justify-between py-2 border-b border-gray-50 dark:border-gray-800">
                <span className="text-gray-500 font-bold">Taxable Purchases (करयोग्य खरिद):</span>
                <span className="font-bold text-gray-900 dark:text-white">
                  Rs. {Number(purchaseSummary.taxable_purchases || 0).toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between py-2 border-b border-gray-50 dark:border-gray-800">
                <span className="text-teal-600 font-bold">13% Input VAT (खरिद कर क्रेडिट):</span>
                <span className="font-bold text-teal-600">
                  Rs. {Number(purchaseSummary.input_vat || 0).toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between py-2 border-b border-gray-50 dark:border-gray-800">
                <span className="text-gray-400 font-bold">Exempt Purchases (कर छुट खरिद):</span>
                <span className="font-bold text-gray-500">
                  Rs. {Number(purchaseSummary.exempt_purchases || 0).toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between py-3 border-t-2 border-gray-200 dark:border-gray-700 text-sm font-black">
                <span className="text-gray-900 dark:text-white">Total Purchases (जम्मा खरिद):</span>
                <span className="text-emerald-600">
                  Rs. {Number(purchaseSummary.total_purchases || 0).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Operating Financial Performance Section */}
        <div className="bg-white dark:bg-restro-gray rounded-3xl border border-gray-100 dark:border-gray-800 p-6 shadow-sm">
          <div className="flex items-center gap-2 pb-4 border-b border-gray-100 dark:border-gray-800">
            <div className="w-8 h-8 rounded-xl bg-purple-100 text-purple-600 dark:bg-purple-950/60 dark:text-purple-300 flex items-center justify-center font-black">
              ३
            </div>
            <div>
              <h3 className="font-black text-base text-gray-900 dark:text-white">
                Monthly Financial Operating Summary (मासिक नाफा नोक्सान झलक)
              </h3>
              <p className="text-[11px] text-gray-400">
                High-level trading and operating performance for {monthObj.name} {selectedFy}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-800/40 rounded-2xl">
              <span className="text-[10px] font-black uppercase text-gray-400 block">Gross Sales</span>
              <span className="text-lg font-black text-gray-900 dark:text-white block mt-1">
                Rs. {Number(financialSummary.gross_sales || 0).toFixed(2)}
              </span>
            </div>

            <div className="p-4 bg-gray-50 dark:bg-gray-800/40 rounded-2xl">
              <span className="text-[10px] font-black uppercase text-gray-400 block">Purchases (COGS)</span>
              <span className="text-lg font-black text-rose-600 block mt-1">
                Rs. {Number(financialSummary.cogs_purchases || 0).toFixed(2)}
              </span>
            </div>

            <div className="p-4 bg-gray-50 dark:bg-gray-800/40 rounded-2xl">
              <span className="text-[10px] font-black uppercase text-gray-400 block">Gross Operating Margin</span>
              <span className="text-lg font-black text-emerald-600 block mt-1">
                Rs. {Number(financialSummary.gross_profit || 0).toFixed(2)}
              </span>
            </div>

            <div className="p-4 bg-gray-50 dark:bg-gray-800/40 rounded-2xl">
              <span className="text-[10px] font-black uppercase text-gray-400 block">Operating Overheads</span>
              <span className="text-lg font-black text-amber-600 block mt-1">
                Rs. {Number(financialSummary.operating_expenses || 0).toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ─── MODAL: CLOSE & LOCK PERIOD ─── */}
      <dialog id="modal-close-period" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box p-0 border border-gray-100 dark:border-gray-800 rounded-3xl overflow-hidden max-w-md">
          <div className="bg-rose-600 p-6 text-white relative">
            <h3 className="text-xl font-black mb-0.5">Close & Lock Accounting Period</h3>
            <p className="text-rose-100 text-xs">
              Locking {monthObj.name} {selectedFy} will freeze all backdated mutations, vouchers, sales, and purchases.
            </p>
          </div>

          <div className="p-6 space-y-4">
            <div className="bg-amber-50 dark:bg-amber-950/30 p-3.5 rounded-2xl border border-amber-200 dark:border-amber-900/40 flex items-start gap-2.5 text-xs text-amber-800 dark:text-amber-200">
              <IconAlertTriangle size={18} className="shrink-0 text-amber-600 mt-0.5" />
              <div>
                <strong>Chartered Accountant Rule:</strong> Once closed, no employee or cashier can insert or modify invoices, purchases, or journal entries for this month without administrative reopen authorization.
              </div>
            </div>

            <div>
              <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">
                Closing Notes / Certification Note
              </label>
              <textarea
                value={closeNotes}
                onChange={(e) => setCloseNotes(e.target.value)}
                placeholder="Audited and approved by Senior Accountant..."
                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-2.5 px-4 text-xs outline-none h-20 resize-none font-medium"
              ></textarea>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => document.getElementById("modal-close-period").close()}
                className="flex-1 py-3 font-bold text-xs text-gray-600 bg-gray-100 dark:bg-gray-800 rounded-2xl hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={handleClosePeriod}
                className="flex-1 py-3 font-bold text-xs text-white bg-rose-600 hover:bg-rose-700 rounded-2xl flex items-center justify-center gap-1.5 shadow-lg shadow-rose-200 dark:shadow-none"
              >
                {isSubmitting ? <span className="loading loading-spinner loading-xs"></span> : <IconLock size={16} />}
                Confirm Lock
              </button>
            </div>
          </div>
        </div>
      </dialog>

      {/* ─── MODAL: REOPEN PERIOD ─── */}
      <dialog id="modal-reopen-period" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box p-0 border border-gray-100 dark:border-gray-800 rounded-3xl overflow-hidden max-w-md">
          <div className="bg-amber-500 p-6 text-gray-950 relative">
            <h3 className="text-xl font-black mb-0.5">Reopen Accounting Period</h3>
            <p className="text-gray-900 text-xs">
              Allow retroactive posting and editing in {monthObj.name} {selectedFy}.
            </p>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">
                Audit Reason for Reopening (Required)
              </label>
              <textarea
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                placeholder="Reason for backdated adjustment or tax rectification..."
                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-2.5 px-4 text-xs outline-none h-20 resize-none font-medium"
              ></textarea>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => document.getElementById("modal-reopen-period").close()}
                className="flex-1 py-3 font-bold text-xs text-gray-600 bg-gray-100 dark:bg-gray-800 rounded-2xl hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSubmitting || !reopenReason.trim()}
                onClick={handleReopenPeriod}
                className="flex-1 py-3 font-bold text-xs text-gray-950 bg-amber-400 hover:bg-amber-500 rounded-2xl flex items-center justify-center gap-1.5 shadow-lg shadow-amber-200 dark:shadow-none"
              >
                {isSubmitting ? <span className="loading loading-spinner loading-xs"></span> : <IconLockOpen size={16} />}
                Confirm Reopen
              </button>
            </div>
          </div>
        </div>
      </dialog>
    </Page>
  );
}
